import * as React from "react";
import { Input, Label, TextField, InputProps } from "react-aria-components";
import styles from "./AtollonTextField.module.css";

export interface IAtollonTextFieldProps
    extends Omit<InputProps, "className" | "style"> {
    label?: string;
    helperText?: string;
    errorMessage?: string;
    placeholder?: string;
    isRequired?: boolean;
    width?: string;
    isDisabled?: boolean;
    labelPosition?: "top" | "left";
    useBrowserValidation?: boolean;
}

export function AtollonTextField(props: IAtollonTextFieldProps) {
    const {
        label,
        helperText,
        errorMessage,
        placeholder,
        isRequired,
        width,
        isDisabled,
        labelPosition = "top",
        useBrowserValidation = false,
        onInvalid,
        onInput,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        ...inputProps
    } = props;

    const [browserError, setBrowserError] = React.useState<string>("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
        if (useBrowserValidation) {
            e.preventDefault(); // Prevent default browser tooltip
            setBrowserError(e.currentTarget.validationMessage);
        }
        onInvalid?.(e);
    };

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
        if (useBrowserValidation && browserError) {
            // Clear browser error when user starts typing
            if (e.currentTarget.validity.valid) {
                setBrowserError("");
            } else {
                setBrowserError(e.currentTarget.validationMessage);
            }
        }
        onInput?.(e);
    };

    const textFieldClassName =
        labelPosition === "left"
            ? `${styles.textField} ${styles.textFieldHorizontal}`
            : styles.textField;

    const displayError =
        errorMessage || (useBrowserValidation ? browserError : "");

    // When there's no visible label, pass aria-label or aria-labelledby to TextField wrapper
    const textFieldAriaProps = !label
        ? {
              ...(ariaLabel && { "aria-label": ariaLabel }),
              ...(ariaLabelledBy && { "aria-labelledby": ariaLabelledBy }),
          }
        : {};

    return (
        <TextField
            className={textFieldClassName}
            style={{ width }}
            isDisabled={isDisabled}
            {...textFieldAriaProps}
        >
            {label && (
                <Label className={styles.label}>{`${label} ${
                    isRequired ? "*" : ""
                }`}</Label>
            )}
            <Input
                ref={inputRef}
                className={styles.input}
                placeholder={placeholder}
                required={isRequired}
                style={{ width: labelPosition === "left" ? undefined : width }}
                disabled={isDisabled}
                onInvalid={handleInvalid}
                onInput={handleInput}
                {...(ariaLabel && { "aria-label": ariaLabel })}
                {...(ariaLabelledBy && { "aria-labelledby": ariaLabelledBy })}
                {...inputProps}
            />
            {helperText && (
                <Label className={styles.helperText}>{helperText}</Label>
            )}
            {displayError && (
                <Label className={styles.errorMessage}>{displayError}</Label>
            )}
        </TextField>
    );
}
